import { Entity } from '../domain/entity.js';
import { Turret } from '../domain/turret.js';
import { getTwistedTreelineStructures } from '../domain/map/ttLayout.js';
import { getAramStructures } from '../domain/map/aramLayout.js';

/**
 * mapStructureFactory — Sole structure spawner (P3-03).
 *
 * Contrato:
 *   createMapStructures(mapId) → Entity[]              // engine_xy (GameEngine)
 *   createMapStructures(mapId, { coordSpace: 'sandbox_xz' }) → Turret[]  // live 3D
 */

const LAYOUT_LOADERS = {
  twisted_treeline_3v3: getTwistedTreelineStructures,
  aram_proving_grounds: getAramStructures,
  test_map: getAramStructures
};

export function createMapStructures(mapId, { coordSpace = 'engine_xy' } = {}) {
  const loader = LAYOUT_LOADERS[mapId] || getAramStructures;
  const blueprints = loader();
  if (coordSpace === 'sandbox_xz') {
    const turrets = blueprints.map(blueprintToTurret);
    // Shared peer list so isStructureVulnerable sees dead parents (P3-11).
    for (const t of turrets) {
      t.structurePeers = turrets;
    }
    return turrets;
  }
  return blueprints.map(blueprintToEntity);
}

/** Prefer native axis; else bridge the other layout's axis (P3-01/P3-02). */
export function resolveEngineXy(bp = {}) {
  const y = Number.isFinite(bp.y) ? bp.y : bp.z;
  return { x: bp.x, y: Number.isFinite(y) ? y : 0 };
}

export function resolveSandboxXz(bp = {}) {
  const z = Number.isFinite(bp.z) ? bp.z : bp.y;
  return { x: bp.x, z: Number.isFinite(z) ? z : 0 };
}

function structureDisplayName(type, team) {
  const side = team === 'blue' ? 'Azul' : 'Rojo';
  if (type === 'nexus') return `Nexo ${side}`;
  if (type === 'inhibitor') return `Inhibidor ${side}`;
  return `Torreta ${side}`;
}

function blueprintToTurret(bp) {
  const type = bp.type || 'turret';
  const { x, z } = resolveSandboxXz(bp);
  const turret = new Turret({
    id: bp.id,
    team: bp.team,
    x,
    z,
    hp: bp.maxHp ?? 2550,
    maxHp: bp.maxHp ?? 2550,
    ad: bp.ad ?? 152,
    attackRange: bp.attackRange ?? 775
  });
  turret.type = type;
  turret.name = structureDisplayName(type, bp.team);
  if (bp.requires) turret.requires = bp.requires;
  if (bp.tier) turret.tier = bp.tier;
  if (bp.respawnTimer) turret.respawnTimer = bp.respawnTimer;
  return turret;
}

function blueprintToEntity(bp) {
  const type = bp.type || 'turret';
  const { x, y } = resolveEngineXy(bp);
  const entity = new Entity({
    id: bp.id,
    name: structureDisplayName(type, bp.team),
    type,
    team: bp.team,
    x,
    y,
    hp: bp.maxHp,
    maxHp: bp.maxHp,
    ad: bp.ad || 0,
    armor: bp.armor || 0,
    mr: bp.mr || 0,
    ms: 0,
    attackRange: bp.attackRange || 0,
    radius: bp.radius || 35
  });

  if (bp.requires) entity.requires = bp.requires;
  if (bp.respawnTimer) entity.respawnTimer = bp.respawnTimer;
  if (bp.tier) entity.tier = bp.tier;
  return entity;
}
